import React, {useState, useEffect, useRef, useLayoutEffect} from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { lightTheme, darkTheme, GlobalStyles } from './theme';
import Home from './components/Home';
import { FaTwitter, FaLinkedin, FaEnvelope } from 'react-icons/fa';

import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {NavLink as RRNavLink}from 'react-router-dom';
import WebPortfolio from './project-pages/Portfolio';
import ProfessorFinder from './project-pages/ProfessorFinder';
import SleepQualityTracker from './project-pages/SleepQualityTracker';
import { Gradient, normalizeColor } from './gradient.jsx';

const Wrapper = ({ children }) => {
  const location = useLocation();

  useLayoutEffect(() => {
    // Scroll to the top of the page when the route changes
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  return children;
};

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
  
`;
const NavBar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-around;
  border-radius: 64px;
  background-color: ${props => props.theme.navBackground};
  backdrop-filter: blur(50px);
  -webkit-backdrop-filter: blur(5px);
  padding: 0.5em 0.4em;
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  border: 0.5px solid ${props => props.theme.navBorder};
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 0.3rem;
    border-radius: 24px;
    width: 95%;  // Take up most of the screen width
    justify-content: space-between; // Spread items evenly
    overflow-x: auto; // Allow horizontal scrolling if needed
    -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS
    &::-webkit-scrollbar {
      display: none; // Hide scrollbar
    }
    scrollbar-width: none; // Hide scrollbar in Firefox
  }
`
const NavLink = styled(RRNavLink)`
  padding: clamp(0.5rem, 2vw, 0.8rem) clamp(0.75rem, 3vw, 1.0rem);
  font-weight: 600;
  font-size: clamp(0.875rem, 2vw, 1rem);
  border-radius: 64px;
  color: inherit;
  text-decoration: none;
  margin: 0 0.2em;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    border-radius: 20px;
    font-size: 0.875rem;
    margin: 0 0.1em;
  }

  &:hover,
  &.active {
    background: ${props => props.theme.navHoverBackground};
    border: 0.5px solid ${props => props.theme.navBorder};
  }
`
const SiteLink = styled(NavLink).attrs({ as: 'a' })`
  // Inherits styles from NavLink
`
const ScrollLink = styled(Link)`
  padding: clamp(0.5rem, 2vw, 0.8rem) clamp(0.75rem, 3vw, 1.0rem);
  font-weight: 600;
  font-size: clamp(0.875rem, 2vw, 1rem);
  border-radius: 64px;
  color: inherit;
  text-decoration: none;
  margin: 0 0.2em;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    border-radius: 20px;
    font-size: 0.875rem;
    margin: 0 0.1em;
  }

  &:hover {
    background: ${props => props.theme.navHoverBackground};
    border: 0.5px solid ${props => props.theme.navBorder};
  }
`
const SocialIcon = styled.a`
  padding: clamp(0.5rem, 2vw, 0.8rem) clamp(0.75rem, 3vw, 1.0rem);
  font-weight: 600;
  font-size: 1.2rem;
  border-radius: 64px;
  color: inherit;
  text-decoration: none;
  margin: 0 0.2em;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    border-radius: 20px;
    font-size: 1rem;
    margin: 0 0.1em;
  }

  &:hover {
    background: ${props => props.theme.navHoverBackground};
    border: 0.5px solid ${props => props.theme.navBorder};
  }
`
function App() {
  const projectsRef = useRef();
  const [theme, setTheme] = useState("light");
  const isDarkTheme = theme === "dark";
  const [showNav, setShowNav] = useState(false);
  const gradientRef = useRef(null);

  const toggleTheme = () => {
    const updatedTheme = isDarkTheme ? "light" : "dark";
    setTheme(updatedTheme);
    localStorage.setItem("theme", updatedTheme);

    //revise location due to bug
    if (projectsRef.current) {
      projectsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
  }
  
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (savedTheme && ["dark", "light"].includes(savedTheme)) {
      setTheme(savedTheme);
    } else if (prefersDark) {
      setTheme("dark");
    }
    const colorSchemeListener = e => {
      setTheme(e.matches ? "dark" : "light");
    };
    const matchMediaDark = window.matchMedia('(prefers-color-scheme: dark)');
    matchMediaDark.addListener(colorSchemeListener);
    return () => {
      matchMediaDark.removeListener(colorSchemeListener);
    };
  }, []);

  useEffect(() => {
    if (!gradientRef.current) {
      const gradient = new Gradient();
      gradient.initGradient("#gradient-canvas");
      gradientRef.current = gradient;
    }
  }, []);

  useEffect(() => {
    if (!gradientRef.current) return;

    const canvas = document.querySelector("#gradient-canvas");
    if (!canvas) return;

    const computedStyle = getComputedStyle(canvas);
    const newColors = ["--gradient-color-1", "--gradient-color-2", "--gradient-color-3"].map(cssPropertyName => {
      let hex = computedStyle.getPropertyValue(cssPropertyName).trim();
      if (4 === hex.length) {
        const hexTemp = hex.substr(1).split("").map(hexTemp => hexTemp + hexTemp).join("");
        hex = `#${hexTemp}`
      }
      return hex && `0x${hex.substr(1)}`
    }).filter(Boolean).map(normalizeColor);

    // Smoothly transition to new colors
    if (gradientRef.current.mesh && gradientRef.current.mesh.material) {
      gradientRef.current.sectionColors = newColors;
      gradientRef.current.mesh.material.uniforms.u_baseColor.value = newColors[0];
      gradientRef.current.mesh.material.uniforms.u_waveLayers.value.forEach((layer, i) => {
        layer.value.color.value = newColors[i + 1];
      });
    }
  }, [theme]);

  useEffect(() => {
    const handleScroll = () => {
      const arrowElement = document.querySelector('#current-role');
      if (arrowElement) {
        const arrowPosition = arrowElement.getBoundingClientRect().top;
        setShowNav(arrowPosition <= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <ThemeProvider theme={isDarkTheme?darkTheme:lightTheme}>
      <>
        <GlobalStyles/>
        <canvas id="gradient-canvas" />
        <Router>
          <Container>
            <NavBar show={showNav}>
              <ScrollLink to="" onClick={()=>document.getElementById('projects').scrollIntoView({behavior: 'smooth'})} >
                Projects
              </ScrollLink>
              <ScrollLink to="" onClick={()=>document.getElementById('about-section').scrollIntoView({behavior: 'smooth'})}>
                About Me
              </ScrollLink>
              {/* <ScrollLink to="" onClick={()=>document.getElementById('experience-section').scrollIntoView({behavior: 'smooth'})}>
                Experience
              </ScrollLink> */}
              <ScrollLink to="" onClick={()=>document.getElementById('art-section').scrollIntoView({behavior: 'smooth'})}>
                Photography
              </ScrollLink>
              <SocialIcon href="https://www.linkedin.com/in/sebastianlopez2/" target="_blank" rel="noopener noreferrer">
                <FaLinkedin />
              </SocialIcon>
              {/* <SocialIcon href="mailto:sdl2@njit.edu">
                <FaEnvelope />
              </SocialIcon> */}
            </NavBar>
          </Container>
          <Wrapper>
          <Routes>
            <Route path="/*" element={<Home/>}/>
            <Route path="/Portfolio" element={<WebPortfolio/>}/>
            <Route path="/ProfessorFinder" element={<ProfessorFinder/>}/>
            <Route path="/SleepQualityTracker" element={<SleepQualityTracker/>}/>
            
          </Routes>
          </Wrapper>
        </Router>
      </>
    </ThemeProvider>
  );
}

export default App;
